import { useEffect, useRef, useState } from 'react';
import { ArrowUp, BadgeCheck, CalendarDays, CircleDot, ExternalLink, Film, FolderClosed, Hash, Link2, MessageSquareWarning, Palette, Send, Music2, Trash2, Users, X, Youtube } from 'lucide-react';
import {
  CONTENT_ROLES, CONTENT_ROLE_LABELS, PLATFORM_CAPTION_LIMITS, PLATFORM_LABELS,
  type ContentItem, type ContentRole, type CustomField, type UpdateContentInput, type YoutubePrivacy,
} from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, PageLoader } from '../../ui/primitives.tsx';
import { chipColor } from '../../ui/MonthGrid.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import {
  useAddContentComment, useContentAction, useContentComments, useContentItem, useEditorialConfig, useMembers, usePublishTiktok, usePublishYoutube, useTiktokCreatorOptions, useUpdateContent,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { relativeTime } from '../../lib/dates.ts';

function useAutosize(ref: React.RefObject<HTMLTextAreaElement>, value: string) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, 44)}px`;
  }, [ref, value]);
}

export function ContentSheet({ contentId, onClose }: { contentId: string | null; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const item = useContentItem(orgId, contentId);
  return (
    <Sheet open={Boolean(contentId)} onClose={onClose} full labelledBy="content-title">
      {item.isLoading && <PageLoader />}
      {item.error && (
        <div className="empty">
          <strong>{item.error instanceof ApiError && item.error.status === 404 ? 'Contenuto non trovato' : 'Impossibile aprire il contenuto'}</strong>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Chiudi</button>
        </div>
      )}
      {item.data && <ContentDetail key={item.data.id} item={item.data} onClose={onClose} />}
    </Sheet>
  );
}

function ContentDetail({ item, onClose }: { item: ContentItem; onClose: () => void }) {
  const { orgId, detail } = useOrgCtx();
  const config = useEditorialConfig(orgId, item.workspaceId).data;
  const members = useMembers(orgId, detail?.role !== 'guest').data ?? [];
  const update = useUpdateContent(orgId);
  const action = useContentAction(orgId);
  const toast = useToast();
  const [title, setTitle] = useState(item.title);
  const [caption, setCaption] = useState(item.caption);
  const [brief, setBrief] = useState(item.brief);
  const [changes, setChanges] = useState<string | null>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const briefRef = useRef<HTMLTextAreaElement>(null);
  useAutosize(titleRef, title);
  useAutosize(captionRef, caption);
  useAutosize(briefRef, brief);
  const can = item.permissions;

  const save = (patch: UpdateContentInput, okMessage?: string) =>
    update.mutate({ id: item.id, patch }, {
      onSuccess: (after) => {
        if (after.state.id !== item.state.id && patch.stateId === undefined) toast('Contenuto modificato dopo la revisione: torna “In revisione”');
        else if (okMessage) toast(okMessage);
      },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Modifica non salvata'),
    });
  const run = (a: Parameters<typeof action.mutate>[0], ok: string) =>
    action.mutate(a, { onSuccess: () => toast(ok), onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita') });

  const selectedChannels = item.channels.map((c) => c.id);
  const limits = item.channels
    .map((c) => ({ label: PLATFORM_LABELS[c.platform], max: PLATFORM_CAPTION_LIMITS[c.platform] }))
    .filter((l): l is { label: string; max: number } => Boolean(l.max));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span className="state-pill" style={{ '--sd': chipColor(item.state.color) } as React.CSSProperties}>
          <span className="state-dot" /> {item.state.name}
        </span>
        <span className="hint" style={{ padding: 0 }}>{item.workspaceName}</span>
        <span style={{ flex: 1 }} />
        {can.delete && (
          <button type="button" className="icon-btn muted" aria-label="Elimina contenuto" onClick={() => {
            if (confirm(`Eliminare "${item.title}" dal PED?`)) action.mutate({ id: item.id, action: 'delete' }, { onSuccess: () => { toast('Contenuto eliminato'); onClose(); } });
          }}><Trash2 size={18} /></button>
        )}
        <button type="button" className="btn btn-plain btn-sm" style={{ fontWeight: 600 }} onClick={onClose}>Fine</button>
      </div>

      <textarea
        id="content-title" ref={titleRef} className="detail-title" rows={1} value={title} readOnly={!can.edit} aria-label="Titolo"
        onChange={(e) => setTitle(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLTextAreaElement).blur())}
        onBlur={() => { const t = title.trim(); if (t && t !== item.title) save({ title: t }); else setTitle(item.title); }}
      />

      <ApprovalCard item={item} changes={changes} setChanges={setChanges} run={run} />

      <div className="card list props">
        <label className="prop">
          <span><CircleDot size={17} /> Stato</span>
          <select value={item.state.id} disabled={!can.edit} onChange={(e) => save({ stateId: e.target.value })}>
            {config?.states.map((s) => <option key={s.id} value={s.id}>{s.name}{s.requiresApproval ? ' ✓' : ''}</option>)}
          </select>
        </label>
        <div className="prop">
          <span><CalendarDays size={17} /> Uscita</span>
          <span className="inline">
            <input type="date" aria-label="Data di pubblicazione" value={item.plannedDate ?? ''} disabled={!can.edit} style={{ width: 'auto', flex: 1, minWidth: 130 }}
              onChange={(e) => save({ plannedDate: e.target.value || null, ...(e.target.value ? {} : { plannedTime: null }) })} />
            {item.plannedDate && (
              <input type="time" aria-label="Ora di pubblicazione" value={item.plannedTime ?? ''} disabled={!can.edit} style={{ width: 'auto' }}
                onChange={(e) => save({ plannedTime: e.target.value || null })} />
            )}
          </span>
        </div>
        <label className="prop">
          <span><Hash size={17} /> Rubrica</span>
          <select value={item.series?.id ?? ''} disabled={!can.edit} onChange={(e) => save({ seriesId: e.target.value || null })}>
            <option value="">Nessuna</option>
            {config?.series.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="prop">
          <span><Film size={17} /> Formato</span>
          <select value={item.format ?? ''} disabled={!can.edit} onChange={(e) => save({ format: e.target.value || null })}>
            <option value="">—</option>
            {[...new Set([...(config?.formats ?? []), ...(item.format ? [item.format] : [])])].map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <div className="prop" style={{ alignItems: 'start', paddingTop: 12, paddingBottom: 12 }}>
          <span style={{ paddingTop: 4 }}><FolderClosed size={17} /> Canali</span>
          <div className="people-picker">
            {config?.channels.length === 0 && <span className="hint">Aggiungi i canali del media nelle impostazioni del PED.</span>}
            {config?.channels.map((c) => {
              const on = selectedChannels.includes(c.id);
              return (
                <button key={c.id} type="button" className="channel-chip" aria-pressed={on} disabled={!can.edit}
                  onClick={() => save({ channelIds: on ? selectedChannels.filter((id) => id !== c.id) : [...selectedChannels, c.id] })}>
                  {PLATFORM_LABELS[c.platform]} <span style={{ opacity: 0.7 }}>{c.handle}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <People item={item} members={members.map((m) => ({ id: m.userId, name: m.displayName }))} onSave={(m) => save({ members: m })} canEdit={can.edit} />

      <h3 className="subhead" style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        Caption <span style={{ flex: 1 }} />
        {limits.map((l) => (
          <span key={l.label} className={`counter${caption.length > l.max ? ' over' : ''}`}>{l.label} {caption.length}/{l.max}</span>
        ))}
        {!limits.length && <span className="counter">{caption.length} caratteri</span>}
      </h3>
      <textarea ref={captionRef} className="textarea" placeholder={can.edit ? 'Scrivi la caption…' : 'Nessuna caption'} value={caption} readOnly={!can.edit}
        aria-label="Caption" onChange={(e) => setCaption(e.target.value)} onBlur={() => caption !== item.caption && save({ caption })} />

      <h3 className="subhead">Materiali</h3>
      <div className="form" style={{ gap: 10 }}>
        <LinkField icon={<Palette size={17} />} label="Link Canva" value={item.designUrl} canEdit={can.edit} onSave={(v) => save({ designUrl: v })} />
        <LinkField icon={<Link2 size={17} />} label="Link video / asset" value={item.assetUrl} canEdit={can.edit} onSave={(v) => save({ assetUrl: v })} />
      </div>

      <h3 className="subhead">Brief / script</h3>
      <textarea ref={briefRef} className="textarea" placeholder={can.edit ? 'Idea, script, riferimenti, note per grafica e video…' : 'Nessun brief'} value={brief}
        readOnly={!can.edit} aria-label="Brief" onChange={(e) => setBrief(e.target.value)} onBlur={() => brief !== item.brief && save({ brief })} />

      {config && config.fields.length > 0 && (
        <>
          <h3 className="subhead">Altri campi</h3>
          <div className="card list">
            {config.fields.map((f) => (
              <CustomFieldRow key={f.id} field={f} value={item.customFields[f.key]} canEdit={can.edit}
                members={members.map((m) => ({ id: m.userId, name: m.displayName }))}
                onSave={(v) => save({ customFields: { [f.key]: v } })} />
            ))}
          </div>
        </>
      )}

      {item.channels.length > 0 && ['scheduled', 'done'].includes(item.state.category) && (
        <>
          <h3 className="subhead">Link pubblicati</h3>
          <div className="form" style={{ gap: 10 }}>
            {item.channels.map((c) => (
              <LinkField key={c.id} icon={<Send size={17} />} label={`${PLATFORM_LABELS[c.platform]} ${c.handle}`} value={c.publishedUrl} canEdit={can.edit}
                onSave={(v) => run({ id: item.id, action: `channels/${c.id}`, body: { publishedUrl: v } }, 'Link salvato')} />
            ))}
          </div>
        </>
      )}

      {item.channels.some((c) => c.platform === 'youtube' || c.platform === 'tiktok') && (
        <>
          <h3 className="subhead">Pubblica</h3>
          <div className="form" style={{ gap: 10 }}>
            {item.channels.filter((c) => c.platform === 'youtube').map((c) => <YoutubePublish key={c.id} item={item} channel={c} />)}
            {item.channels.filter((c) => c.platform === 'tiktok').map((c) => <TiktokPublish key={c.id} item={item} channel={c} />)}
          </div>
        </>
      )}

      <p className="hint" style={{ marginTop: 14 }}>Creato da {item.createdBy.displayName} · {relativeTime(item.createdAt)}</p>
      <ContentComments item={item} />
    </>
  );
}

function ApprovalCard({ item, changes, setChanges, run }: {
  item: ContentItem;
  changes: string | null;
  setChanges: (v: string | null) => void;
  run: (a: { id: string; action: 'submit' | 'approve' | 'request-changes'; body?: unknown }, ok: string) => void;
}) {
  const can = item.permissions;
  const a = item.approval;
  if (a.status === 'approved') {
    return (
      <div className="approval approved">
        <BadgeCheck size={22} color="var(--green)" />
        <span className="grow"><strong>Revisionato</strong> da {a.by?.displayName}{a.at ? ` · ${relativeTime(a.at)}` : ''}</span>
      </div>
    );
  }
  const inReview = item.state.category === 'review';
  return (
    <div className={`approval ${a.status === 'changes_requested' ? 'changes' : 'pending'}`} style={{ flexWrap: 'wrap' }}>
      {a.status === 'changes_requested' ? <MessageSquareWarning size={22} color="var(--red)" /> : <BadgeCheck size={22} color="var(--orange)" />}
      <span className="grow">
        <strong>{a.status === 'changes_requested' ? 'Modifiche richieste' : inReview ? 'In attesa di revisione' : 'Non ancora revisionato'}</strong>
        <div className="hint" style={{ padding: 0 }}>Si può programmare solo dopo la revisione del PM.</div>
      </span>
      <span className="actions">
        {can.edit && !inReview && <button type="button" className="btn btn-secondary btn-sm" onClick={() => run({ id: item.id, action: 'submit' }, 'Inviato in revisione')}>Invia in revisione</button>}
        {can.approve && (
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => run({ id: item.id, action: 'approve' }, 'Contenuto revisionato')}>Segna come revisionato</button>
            {changes === null && <button type="button" className="btn btn-plain btn-sm" onClick={() => setChanges('')}>Chiedi modifiche</button>}
          </>
        )}
      </span>
      {changes !== null && (
        <form style={{ display: 'flex', gap: 8, width: '100%' }} onSubmit={(e) => {
          e.preventDefault();
          if (changes.trim()) { run({ id: item.id, action: 'request-changes', body: { comment: changes } }, 'Modifiche richieste'); setChanges(null); }
        }}>
          <input className="input" autoFocus placeholder="Cosa va cambiato?" value={changes} onChange={(e) => setChanges(e.target.value)} aria-label="Modifiche richieste" />
          <button className="btn btn-danger btn-sm" style={{ height: 'auto' }} disabled={!changes.trim()}>Invia</button>
          <button type="button" className="icon-btn muted" aria-label="Annulla" onClick={() => setChanges(null)}><X size={18} /></button>
        </form>
      )}
    </div>
  );
}

function People({ item, members, onSave, canEdit }: { item: ContentItem; members: { id: string; name: string }[]; onSave: (m: { userId: string; role: ContentRole }[]) => void; canEdit: boolean }) {
  const [role, setRole] = useState<ContentRole>('owner');
  const current = item.members.map((m) => ({ userId: m.userId, role: m.role }));
  return (
    <>
      <h3 className="subhead"><Users size={13} style={{ verticalAlign: -2 }} /> Persone</h3>
      <div className="card list">
        {item.members.map((m) => (
          <div key={`${m.userId}-${m.role}`} className="list-row">
            <Avatar id={m.userId} name={m.displayName} size={30} />
            <span className="grow">{m.displayName}<div className="secondary">{CONTENT_ROLE_LABELS[m.role]}</div></span>
            {canEdit && (
              <button type="button" className="icon-btn muted" aria-label={`Rimuovi ${m.displayName}`}
                onClick={() => onSave(current.filter((c) => !(c.userId === m.userId && c.role === m.role)))}><X size={17} /></button>
            )}
          </div>
        ))}
        {canEdit && (
          <div className="list-row" style={{ gap: 8 }}>
            <select className="select" style={{ width: 'auto', minHeight: 34, padding: '4px 30px 4px 10px', fontSize: 14 }} value={role} onChange={(e) => setRole(e.target.value as ContentRole)} aria-label="Ruolo">
              {CONTENT_ROLES.map((r) => <option key={r} value={r}>{CONTENT_ROLE_LABELS[r]}</option>)}
            </select>
            <select className="select" style={{ flex: 1, minHeight: 34, padding: '4px 30px 4px 10px', fontSize: 14 }} value="" aria-label="Persona"
              onChange={(e) => e.target.value && onSave([...current, { userId: e.target.value, role }])}>
              <option value="">Aggiungi persona…</option>
              {members.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {!canEdit && !item.members.length && <p className="hint" style={{ padding: 14, margin: 0 }}>Nessuno assegnato</p>}
      </div>
    </>
  );
}

const plannedMs = (item: ContentItem) => (item.plannedDate ? new Date(`${item.plannedDate}T${item.plannedTime ?? '00:00'}`).getTime() : null);

/** Upload of the item's video to the YouTube channel assigned to its workspace. Title and description are
 * the item's title and caption; a planned date ahead becomes YouTube's own scheduled publication. */
function YoutubePublish({ item, channel }: { item: ContentItem; channel: ContentItem['channels'][number] }) {
  const { orgId } = useOrgCtx();
  const publish = usePublishYoutube(orgId);
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [privacy, setPrivacy] = useState<YoutubePrivacy>('public');
  const [progress, setProgress] = useState<number | null>(null);
  const label = `YouTube ${channel.handle}`;

  if (channel.status === 'published' || channel.status === 'scheduled') {
    return (
      <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Youtube size={18} />
        <span style={{ flex: 1 }}>{label}: {channel.status === 'scheduled' ? 'caricato, uscita programmata su YouTube' : 'caricato'}</span>
        {channel.publishedUrl && <a className="btn btn-secondary btn-sm" href={channel.publishedUrl} target="_blank" rel="noopener noreferrer">Apri</a>}
      </div>
    );
  }
  const approved = item.approval.status === 'approved' || item.approval.status === 'not_required';
  const at = plannedMs(item);
  const scheduled = at !== null && at > Date.now() + 15 * 60_000;
  const uploading = publish.isPending;

  const submit = () => {
    if (!file) return;
    setProgress(0);
    publish.mutate({ id: item.id, channelId: channel.id, file, privacy, onProgress: setProgress }, {
      onSuccess: (r) => {
        setFile(null);
        if (r.privacy !== r.requestedPrivacy && !r.scheduledFor) toast('Caricato, ma YouTube lo ha reso privato: il progetto Google non è ancora verificato da YouTube');
        else toast(r.scheduledFor ? `Caricato: YouTube lo pubblicherà il ${new Date(r.scheduledFor).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}` : 'Video pubblicato su YouTube');
      },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Caricamento non riuscito'),
      onSettled: () => setProgress(null),
    });
  };

  return (
    <div className="card card-pad form" style={{ gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}><Youtube size={18} /> {label}</div>
      {!item.permissions.edit ? <p className="hint" style={{ padding: 0 }}>Non ancora caricato.</p>
      : !approved ? <p className="hint" style={{ padding: 0 }}>Il contenuto va approvato prima di caricarlo su YouTube.</p>
      : (
        <>
          <p className="hint" style={{ padding: 0 }}>
            Titolo e descrizione: titolo e caption di questo contenuto.{' '}
            {scheduled ? `YouTube lo pubblicherà da solo il ${new Date(at!).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })} (data di uscita).` : 'Uscita subito dopo il caricamento.'}
          </p>
          <input type="file" accept="video/*" aria-label="File video" disabled={uploading} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {!scheduled && (
            <label className="field">
              <span>Visibilità</span>
              <select value={privacy} disabled={uploading} onChange={(e) => setPrivacy(e.target.value as YoutubePrivacy)}>
                <option value="public">Pubblico</option>
                <option value="unlisted">Non in elenco</option>
                <option value="private">Privato</option>
              </select>
            </label>
          )}
          <UploadProgress progress={progress} platform="YouTube" />
          <button type="button" className="btn btn-primary" disabled={!file || uploading} onClick={submit}>
            {uploading ? 'Caricamento…' : 'Carica su YouTube'}
          </button>
        </>
      )}
    </div>
  );
}

const TIKTOK_PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Tutti', MUTUAL_FOLLOW_FRIENDS: 'Amici', FOLLOWER_OF_CREATOR: 'Follower', SELF_ONLY: 'Solo io',
};

function UploadProgress({ progress, platform }: { progress: number | null; platform: string }) {
  if (progress === null) return null;
  return (
    <div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--fill)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(progress * 100)}%`, height: '100%', background: 'var(--blue)' }} />
      </div>
      <p className="hint" style={{ padding: 0, marginTop: 4 }}>
        {progress < 1 ? `Caricamento ${Math.round(progress * 100)}%` : `Invio a ${platform} in corso, non chiudere la scheda…`}
      </p>
    </div>
  );
}

/** Direct post to the TikTok account assigned to the item's workspace. TikTok's posting guidelines: show
 * who it posts as, no default visibility, interactions off unless chosen, commercial content disclosure,
 * and the music/branded content consent line. TikTok has no scheduling: it goes out on upload. */
function TiktokPublish({ item, channel }: { item: ContentItem; channel: ContentItem['channels'][number] }) {
  const { orgId } = useOrgCtx();
  const toast = useToast();
  const publish = usePublishTiktok(orgId);
  const label = `TikTok ${channel.handle}`;
  const approved = item.approval.status === 'approved' || item.approval.status === 'not_required';
  const pending = channel.status !== 'published' && channel.status !== 'scheduled';
  const options = useTiktokCreatorOptions(orgId, item.id, channel.id, item.permissions.edit && approved && pending);
  const [file, setFile] = useState<File | null>(null);
  const [privacy, setPrivacy] = useState('');
  const [allow, setAllow] = useState({ comment: false, duet: false, stitch: false });
  const [commercial, setCommercial] = useState(false);
  const [brand, setBrand] = useState({ organic: false, branded: false });
  const [progress, setProgress] = useState<number | null>(null);

  if (!pending) {
    return (
      <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Music2 size={18} />
        <span style={{ flex: 1 }}>{label}: pubblicato</span>
        {channel.publishedUrl && <a className="btn btn-secondary btn-sm" href={channel.publishedUrl} target="_blank" rel="noopener noreferrer">Apri</a>}
      </div>
    );
  }
  const header = <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}><Music2 size={18} /> {label}</div>;
  if (!item.permissions.edit) return <div className="card card-pad form" style={{ gap: 10 }}>{header}<p className="hint" style={{ padding: 0 }}>Non ancora pubblicato.</p></div>;
  if (!approved) return <div className="card card-pad form" style={{ gap: 10 }}>{header}<p className="hint" style={{ padding: 0 }}>Il contenuto va approvato prima di pubblicarlo su TikTok.</p></div>;
  if (options.error) {
    return <div className="card card-pad form" style={{ gap: 10 }}>{header}<p className="hint" style={{ padding: 0 }}>{options.error instanceof ApiError ? options.error.message : 'TikTok non risponde: riprova più tardi.'}</p></div>;
  }
  if (!options.data) return <div className="card card-pad form" style={{ gap: 10 }}>{header}<p className="hint" style={{ padding: 0 }}>Carico le impostazioni del profilo TikTok…</p></div>;

  const o = options.data;
  const uploading = publish.isPending;
  const brandMissing = commercial && !brand.organic && !brand.branded;
  const submit = () => {
    if (!file || !privacy || brandMissing) return;
    setProgress(0);
    const flag = (b: boolean) => (b ? '1' : '0') as '0' | '1';
    publish.mutate({
      id: item.id, channelId: channel.id, file, onProgress: setProgress,
      options: {
        privacy, allowComment: flag(allow.comment), allowDuet: flag(allow.duet), allowStitch: flag(allow.stitch),
        brandOrganic: flag(commercial && brand.organic), brandedContent: flag(commercial && brand.branded),
      },
    }, {
      onSuccess: (r) => { setFile(null); toast(r.complete ? 'Video pubblicato su TikTok' : 'Video inviato: TikTok lo sta ancora elaborando'); },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Pubblicazione non riuscita'),
      onSettled: () => setProgress(null),
    });
  };
  const check = (key: 'comment' | 'duet' | 'stitch', text: string, disabled: boolean) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={!disabled && allow[key]} disabled={disabled || uploading} onChange={(e) => setAllow({ ...allow, [key]: e.target.checked })} />
      {text}{disabled ? ' (disattivato nel profilo)' : ''}
    </label>
  );

  return (
    <div className="card card-pad form" style={{ gap: 10 }}>
      {header}
      <p className="hint" style={{ padding: 0 }}>
        Pubblichi come <strong>{o.nickname}</strong>{o.username ? ` (@${o.username})` : ''}. Didascalia: la caption di questo contenuto.
        TikTok non permette di programmare: il video esce appena caricato.{o.maxDurationSec ? ` Durata massima ${Math.floor(o.maxDurationSec / 60)} min.` : ''}
      </p>
      <input type="file" accept="video/mp4,video/quicktime,video/webm" aria-label="File video TikTok" disabled={uploading} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <label className="field">
        <span>Chi può vedere il video</span>
        <select value={privacy} disabled={uploading} onChange={(e) => setPrivacy(e.target.value)}>
          <option value="" disabled>Scegli…</option>
          {o.privacyOptions.map((p) => (
            <option key={p} value={p} disabled={commercial && brand.branded && p === 'SELF_ONLY'}>{TIKTOK_PRIVACY_LABELS[p] ?? p}</option>
          ))}
        </select>
      </label>
      {check('comment', 'Consenti commenti', o.commentDisabled)}
      {check('duet', 'Consenti duetti', o.duetDisabled)}
      {check('stitch', 'Consenti stitch', o.stitchDisabled)}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
        <input type="checkbox" checked={commercial} disabled={uploading} onChange={(e) => setCommercial(e.target.checked)} />
        Contenuto commerciale (promuove un brand, un prodotto o un servizio)
      </label>
      {commercial && (
        <div style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={brand.organic} disabled={uploading} onChange={(e) => setBrand({ ...brand, organic: e.target.checked })} />
            Il tuo brand (etichetta “Contenuto promozionale”)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={brand.branded} disabled={uploading} onChange={(e) => {
              setBrand({ ...brand, branded: e.target.checked });
              if (e.target.checked && privacy === 'SELF_ONLY') setPrivacy('');
            }} />
            Contenuto brandizzato per terzi (etichetta “Partnership retribuita”; non può essere “Solo io”)
          </label>
          {brandMissing && <p className="hint" style={{ padding: 0, color: 'var(--orange)' }}>Indica di che tipo di contenuto commerciale si tratta.</p>}
        </div>
      )}
      <p className="hint" style={{ padding: 0 }}>
        Pubblicando accetti la{' '}
        <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer">Music Usage Confirmation</a>
        {commercial && brand.branded && <> e la <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noopener noreferrer">Branded Content Policy</a></>} di TikTok.
      </p>
      <UploadProgress progress={progress} platform="TikTok" />
      <button type="button" className="btn btn-primary" disabled={!file || !privacy || brandMissing || uploading} onClick={submit}>
        {uploading ? 'Pubblicazione…' : 'Pubblica su TikTok'}
      </button>
    </div>
  );
}

function LinkField({ icon, label, value, canEdit, onSave }: { icon: React.ReactNode; label: string; value: string | null; canEdit: boolean; onSave: (v: string | null) => void }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  return (
    <label className="field">
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{icon} {label}</span>
      <span className="link-field">
        <input className="input" type="url" inputMode="url" placeholder="https://" value={v} readOnly={!canEdit}
          onChange={(e) => setV(e.target.value)} onBlur={() => (v.trim() || null) !== value && onSave(v.trim() || null)} />
        {value && <a className="icon-btn" href={value} target="_blank" rel="noopener noreferrer" aria-label={`Apri ${label}`}><ExternalLink size={18} /></a>}
      </span>
    </label>
  );
}

function CustomFieldRow({ field, value, canEdit, members, onSave }: { field: CustomField; value: unknown; canEdit: boolean; members: { id: string; name: string }[]; onSave: (v: unknown) => void }) {
  const [text, setText] = useState(typeof value === 'string' || typeof value === 'number' ? String(value) : '');
  useEffect(() => setText(typeof value === 'string' || typeof value === 'number' ? String(value) : ''), [value]);
  const input = () => {
    switch (field.fieldType) {
      case 'checkbox':
        return <button type="button" role="switch" className="switch" aria-checked={value === true} disabled={!canEdit} onClick={() => onSave(value !== true)} aria-label={field.label} />;
      case 'select':
        return <select value={typeof value === 'string' ? value : ''} disabled={!canEdit} onChange={(e) => onSave(e.target.value || null)}>
          <option value="">—</option>{field.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
      case 'multi_select': {
        const arr = Array.isArray(value) ? (value as string[]) : [];
        return <span className="people-picker">{field.options.map((o) => (
          <button key={o} type="button" className="channel-chip" aria-pressed={arr.includes(o)} disabled={!canEdit}
            onClick={() => { const next = arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]; onSave(next.length ? next : null); }}>{o}</button>
        ))}</span>;
      }
      case 'user':
        return <select value={typeof value === 'string' ? value : ''} disabled={!canEdit} onChange={(e) => onSave(e.target.value || null)}>
          <option value="">—</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>;
      case 'date':
        return <input type="date" value={typeof value === 'string' ? value : ''} disabled={!canEdit} onChange={(e) => onSave(e.target.value || null)} />;
      default:
        return <input type={field.fieldType === 'number' ? 'number' : field.fieldType === 'url' ? 'url' : 'text'} value={text} readOnly={!canEdit}
          placeholder={field.fieldType === 'url' ? 'https://' : '—'} onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const next = text.trim() === '' ? null : field.fieldType === 'number' ? Number(text) : text.trim();
            if (next !== (value ?? null)) onSave(next);
          }} />;
    }
  };
  return (
    <label className="prop" style={field.fieldType === 'multi_select' ? { alignItems: 'start', paddingTop: 12, paddingBottom: 12 } : undefined}>
      <span>{field.label}</span>
      {input()}
    </label>
  );
}

function ContentComments({ item }: { item: ContentItem }) {
  const { orgId } = useOrgCtx();
  const comments = useContentComments(orgId, item.id);
  const add = useAddContentComment(orgId, item.id);
  const [body, setBody] = useState('');
  const send = () => { const t = body.trim(); if (t) add.mutate(t, { onSuccess: () => setBody('') }); };
  return (
    <>
      <h3 className="subhead">Commenti</h3>
      <div className="comments">
        {comments.data?.length === 0 && <p className="hint">Nessun commento. Feedback su caption e grafiche qui, non su WhatsApp: resta tutto nello storico.</p>}
        {comments.data?.map((c) => (
          <div className="comment" key={c.id}>
            <Avatar id={c.author.id} name={c.author.displayName} size={30} />
            <div className="comment-bubble">
              <div className="comment-head"><strong>{c.author.displayName}</strong><time dateTime={c.createdAt}>{relativeTime(c.createdAt)}</time></div>
              <div className="comment-body">{c.body}</div>
            </div>
          </div>
        ))}
      </div>
      {item.permissions.comment && (
        <div className="composer" style={{ marginTop: 16 }}>
          <textarea className="textarea" rows={1} placeholder="Scrivi un commento" value={body} aria-label="Nuovo commento"
            onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.metaKey || e.ctrlKey) && send()} />
          <button type="button" className="send-btn" aria-label="Invia commento" disabled={!body.trim() || add.isPending} onClick={send}><ArrowUp size={20} strokeWidth={2.5} /></button>
        </div>
      )}
    </>
  );
}

